import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteModal from "./QuoteModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agglos-producer/artifacts/zs2rc3fd_10.png";

const navLinks = [
  { name: "Accueil", path: "/" },
  { name: "Produits", path: "/produits" },
  { name: "Services", path: "/services" },
  { name: "À Propos", path: "/a-propos" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navBgClass = isScrolled || !isHomePage
    ? "bg-white shadow-lg"
    : "bg-white/95 backdrop-blur-sm";

  return (
    <>
      {/* Top Bar */}
      <div className="bg-transparent text-white py-2 text-sm hidden md:block absolute w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:+221775472760" className="flex items-center gap-2 hover:text-[#FFD100] transition-colors" data-testid="topbar-phone">
              <Phone className="w-4 h-4 text-[#FFD100]" />
              <span>+221 77 547 27 60</span>
            </a>
            <a href="mailto:contact@agrabat.sn" className="flex items-center gap-2 hover:text-[#FFD100] transition-colors" data-testid="topbar-email">
              <Mail className="w-4 h-4 text-[#FFD100]" />
              <span>contact@agrabat.sn</span>
            </a>
          </div>
          <div className="text-white/80">
            190 Avenue Lamine Gueye, Thiès
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${navBgClass} ${isHomePage && !isScrolled ? "top-0 md:top-10" : "top-0"}`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
              <img
                src={LOGO_URL}
                alt="AGRABAT"
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                    location.pathname === link.path
                      ? "text-[#FFD100]"
                      : "text-[#1E3A5F] hover:text-[#FFD100]"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <Button
                onClick={() => setIsQuoteModalOpen(true)}
                className="bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none px-6 py-2 font-bold uppercase tracking-wider"
                data-testid="nav-quote-btn"
              >
                Demander un Devis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-[#1E3A5F] p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute top-full left-0 right-0 bg-[#1E3A5F] shadow-lg transition-all duration-300 ${
            isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-lg font-medium uppercase tracking-wider py-2 ${
                  location.pathname === link.path
                    ? "text-[#FFD100]"
                    : "text-white hover:text-[#FFD100]"
                }`}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsQuoteModalOpen(true);
              }}
              className="w-full bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none py-4 font-bold uppercase tracking-wider mt-4"
              data-testid="mobile-quote-btn"
            >
              Demander un Devis
            </Button>
          </div>
        </div>
      </nav>

      {/* Quote Modal */}
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </>
  );
}
